import { useEffect, useMemo, useRef, useState } from 'react';
import { embedDashboard } from '@superset-ui/embedded-sdk';
import { useNavigate } from 'react-router-dom';
import {
  faArrowLeft,
  faBars,
  faCircleQuestion,
  faDownload,
  faShareNodes,
  faSliders,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import '../css/ApacheSuperset.css';
import logo from '../navigation/images/logoheader.svg';
import api from '../services/api';
import { useZona } from '../context/ZonaContext';
import SupersetSituationView from './SupersetSituationView';
import SupersetContactsView from './SupersetContactsView';
import SupersetTerritorialView from './SupersetTerritorialView';
import SupersetSectorView from './SupersetSectorView';
import SupersetDataQualityView from './SupersetDataQualityView';
import SupersetPathologyView from './SupersetPathologyView';
import SupersetDownloadView from './SupersetDownloadView';

const SUPERSET_DASHBOARD_URL =
  process.env.REACT_APP_SUPERSET_DASHBOARD_URL ||
  'http://localhost:8088/superset/dashboard/12/?native_filters_key=GDeF3V_tDFg';

const SUPERSET_EMBEDDED_DASHBOARD_UUID =
  process.env.REACT_APP_SUPERSET_EMBEDDED_DASHBOARD_UUID || '';

const SUPERSET_DOMAIN =
  new URL(SUPERSET_DASHBOARD_URL, window.location.origin).origin;

const LOCALITY_PROVINCE_FALLBACK = {
  'la plata': 'Buenos Aires',
  'mar del plata': 'Buenos Aires',
};

const NATIVE_FILTER_IDS = {
  patologia: 'NATIVE_FILTER-vec-patologia',
  zona: 'NATIVE_FILTER-vec-zona',
  area: 'NATIVE_FILTER-vec-area-programatica',
  provincia: 'NATIVE_FILTER-vec-provincia',
  departamento: 'NATIVE_FILTER-vec-departamento',
  municipio: 'NATIVE_FILTER-vec-municipio',
  anio: 'NATIVE_FILTER-vec-anio',
  semana: 'NATIVE_FILTER-vec-semana',
};

const toNumericId = (value) => {
  const numericValue = Number(value);
  return Number.isInteger(numericValue) && numericValue > 0 ? numericValue : null;
};

const normalizeLocationName = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLocaleLowerCase()
  .trim();

const findZoneByName = (zones, name) => {
  const normalizedName = normalizeLocationName(name);
  return (zones || []).find((zone) => {
    const zoneName = normalizeLocationName(zone.Nombre);
    return zoneName === normalizedName || zoneName.includes(normalizedName) || normalizedName.includes(zoneName);
  });
};

const encodeRisonValue = (value) => {
  const stringValue = String(value);
  if (/^-?\d+(\.\d+)?$/.test(stringValue)) return stringValue;
  return `'${stringValue.replace(/!/g, '!!').replace(/'/g, "!'")}'`;
};

const encodeRisonString = (value) =>
  `'${String(value).replace(/!/g, '!!').replace(/'/g, "!'")}'`;

const buildNativeFilter = (filterId, column, rawValue) => {
  const values = (Array.isArray(rawValue) ? rawValue : [rawValue]).filter(
    (value) => value !== '' && value != null && !/^Tod[oa]s$/i.test(String(value))
  );
  if (!values.length) return null;
  const encodedValues = values.map(encodeRisonValue).join(',');
  // Superset llama String.prototype.includes sobre label. Incluso cuando el
  // valor filtrado es un ID numérico, la etiqueta debe conservarse como texto.
  const label = encodeRisonString(values.join(', '));
  return `${filterId}:(extraFormData:(filters:!((col:${column},op:IN,val:!(${encodedValues})))),` +
    `filterState:(label:${label},validateStatus:!f,value:!(${encodedValues})),` +
    `id:${filterId},ownState:())`;
};

const withEmbedMode = (source, scope, filters = {}) => {
  const url = new URL(source, window.location.origin);
  url.searchParams.set('standalone', '2');
  url.searchParams.set('show_filters', '0');
  url.searchParams.set('expand_filters', '0');
  url.searchParams.delete('native_filters_key');

  const nativeFilters = [
    scope.idPatologia &&
      buildNativeFilter(NATIVE_FILTER_IDS.patologia, 'idPatologia', scope.idPatologia),
    scope.idZonaSanitaria &&
      buildNativeFilter(NATIVE_FILTER_IDS.zona, 'idZonaSanitaria', scope.idZonaSanitaria),
    scope.idAreaProgramatica &&
      buildNativeFilter(NATIVE_FILTER_IDS.area, 'idAreaProgramatica', scope.idAreaProgramatica),
    buildNativeFilter(NATIVE_FILTER_IDS.provincia, 'Provincia', filters.provincia),
    buildNativeFilter(NATIVE_FILTER_IDS.departamento, 'Departamento', filters.departamento),
    buildNativeFilter(NATIVE_FILTER_IDS.municipio, 'Municipio', filters.municipio),
    buildNativeFilter(NATIVE_FILTER_IDS.anio, 'AnioEpidemiologico', filters.anio),
    buildNativeFilter(NATIVE_FILTER_IDS.semana, 'SemanaEpidemiologica', filters.semana),
  ].filter(Boolean);

  if (nativeFilters.length > 0) {
    url.searchParams.set('native_filters', `(${nativeFilters.join(',')})`);
  }
  return url.toString();
};

const getInitials = (name) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase();

const MultiFilterSelect = ({ label, values, selected, onChange }) => {
  const selectedValues = Array.isArray(selected) ? selected : [];
  const toggleValue = (value) => onChange(
    selectedValues.includes(value)
      ? selectedValues.filter((item) => item !== value)
      : [...selectedValues, value]
  );
  return (
    <details className="superset-multi-filter">
      <summary title={selectedValues.join(', ')}>
        {selectedValues.length ? `${selectedValues.length} seleccionados` : 'Todos'}
      </summary>
      <div className="superset-multi-filter__options">
        {values.length ? values.map((value) => (
          <label key={value}>
            <input
              type="checkbox"
              checked={selectedValues.includes(value)}
              onChange={() => toggleValue(value)}
            />
            <span>{value}</span>
          </label>
        )) : <small>Sin opciones disponibles</small>}
        {selectedValues.length > 0 && (
          <button type="button" onClick={() => onChange([])}>Limpiar selección</button>
        )}
      </div>
      <span className="sr-only">{label}</span>
    </details>
  );
};

const ApacheSuperset = () => {
  const navigate = useNavigate();
  const { zonaActiva: zonaActivaContext } = useZona();
  const defaultScope = useMemo(
    () => ({
      idUsuario: toNumericId(localStorage.getItem('idUsuario')),
      idPatologia: toNumericId(localStorage.getItem('idPatologiaSeleccionada')),
      idZonaSanitaria: null,
      idAreaProgramatica: null,
    }),
    []
  );
  const [dashboardKey, setDashboardKey] = useState(0);
  const [dashboardZoom, setDashboardZoom] = useState(1);
  const [appliedScope, setAppliedScope] = useState(defaultScope);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 760px)').matches);
  const [filtersVisible, setFiltersVisible] = useState(() => !window.matchMedia('(max-width: 760px)').matches);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeView, setActiveView] = useState('dashboard');
  const [options, setOptions] = useState({
    patologias: [], zonas: [], localidades: [], estados: [], areas: [],
    sectores: [], subsectores: [], instituciones: [],
  });
  const [filters, setFilters] = useState({
    patologia: localStorage.getItem('patologiaActiva') || '',
    anio: String(new Date().getFullYear()),
    semana: [],
    provincia: '',
    departamento: [],
    municipio: [],
    zona: localStorage.getItem('zonaActiva') || '',
    area: 'Todas',
    efector: 'Todos',
    unidad: 'Todas',
    sector: 'Todos',
    subsector: 'Todos',
    estadoCaso: '',
    estadoSeguimiento: '',
  });
  const [appliedFilters, setAppliedFilters] = useState(() => ({
    patologia: localStorage.getItem('patologiaActiva') || '',
    anio: String(new Date().getFullYear()),
    semana: [], provincia: '', departamento: [], municipio: [],
    zona: localStorage.getItem('zonaActiva') || '',
    area: 'Todas', efector: 'Todos', sector: 'Todos', subsector: 'Todos',
    estadoCaso: '', estadoSeguimiento: '',
  }));
  const dashboardFilters = useMemo(() => ({
    ...appliedFilters,
    provincia: appliedFilters.provincia || filters.provincia,
    departamento: appliedFilters.departamento?.length
      ? appliedFilters.departamento
      : filters.departamento,
    municipio: appliedFilters.municipio?.length
      ? appliedFilters.municipio
      : filters.municipio,
  }), [appliedFilters, filters.provincia, filters.departamento, filters.municipio]);
  const dashboardUrl = useMemo(
    () => withEmbedMode(SUPERSET_DASHBOARD_URL, appliedScope, dashboardFilters),
    [appliedScope, dashboardFilters]
  );
  const dashboardMountRef = useRef(null);

  const embeddedUrlParams = useMemo(() => {
    const url = new URL(dashboardUrl, window.location.origin);
    return Object.fromEntries(url.searchParams.entries());
  }, [dashboardUrl]);

  const userName = localStorage.getItem('nombreUsuario') || '';
  const activePathology = localStorage.getItem('patologiaActiva') || '';
  const initials = getInitials(userName);

  useEffect(() => {
    document.body.classList.add('apache-superset-active');
    return () => document.body.classList.remove('apache-superset-active');
  }, []);

  useEffect(() => {
    if (activeView !== 'dashboard' || !dashboardMountRef.current) {
      return undefined;
    }

    if (!SUPERSET_EMBEDDED_DASHBOARD_UUID) {
      console.error(
        'Falta REACT_APP_SUPERSET_EMBEDDED_DASHBOARD_UUID'
      );
      return undefined;
    }

    const mountPoint = dashboardMountRef.current;

    let disposed = false;
    let embeddedDashboard = null;

    const mountDashboard = async () => {
      try {
        mountPoint.innerHTML = '';
        mountPoint.classList.remove(
          'superset-shell__dashboard--error'
        );

        const instance = await embedDashboard({
          id: SUPERSET_EMBEDDED_DASHBOARD_UUID,
          supersetDomain: SUPERSET_DOMAIN,
          mountPoint,

          fetchGuestToken: async () => {
            const response = await api.get(
              '/superset/guest-token'
            );

            const token = response.data?.token;

            if (!token) {
              throw new Error(
                'El backend VEC no devolvió Guest Token'
              );
            }

            return token;
          },

          dashboardUiConfig: {
            hideTitle: true,
            hideTab: true,
            filters: {
              visible: false,
              expanded: false,
            },
            urlParams: embeddedUrlParams,
          },

          iframeAllowExtras: [
            'clipboard-write',
          ],
        });

        if (disposed) {
          instance?.unmount?.();
          return;
        }

        embeddedDashboard = instance;

        const iframe = mountPoint.querySelector('iframe');

        if (iframe) {
          iframe.title =
            'Panel de estadísticas epidemiológicas';
          iframe.style.width = '100%';
          iframe.style.height = '100%';
          iframe.style.border = '0';
        }
      } catch (error) {
        console.error(
          'Error al incrustar dashboard Superset:',
          error
        );

        if (!disposed) {
          mountPoint.classList.add(
            'superset-shell__dashboard--error'
          );
          mountPoint.textContent =
            'No se pudo cargar el panel de estadísticas.';
        }
      }
    };

    mountDashboard();

    return () => {
      disposed = true;

      try {
        embeddedDashboard?.unmount?.();
      } catch (error) {
        console.warn(
          'No se pudo desmontar Superset Embedded:',
          error
        );
      }

      mountPoint.innerHTML = '';
    };
  }, [
    activeView,
    dashboardKey,
    embeddedUrlParams,
  ]);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 760px)');
    const handleViewportChange = (event) => {
      setIsMobile(event.matches);
      if (event.matches) setFiltersVisible(false);
    };
    media.addEventListener('change', handleViewportChange);
    return () => media.removeEventListener('change', handleViewportChange);
  }, []);

  useEffect(() => {
    if (options.patologias.length === 0 || options.zonas.length === 0) return;

    const selectedPathology = options.patologias.find(
      (item) => String(item.Nombre).toLocaleLowerCase() === filters.patologia.toLocaleLowerCase()
    );
    const selectedZone = findZoneByName(options.zonas, filters.zona);

    setAppliedScope((current) => ({
      idUsuario: current.idUsuario || defaultScope.idUsuario,
      idPatologia:
        toNumericId(selectedPathology?.idPatologia ?? selectedPathology?.IdPatologia) ||
        current.idPatologia,
      idZonaSanitaria:
        toNumericId(selectedZone?.IdZonaSanitaria ?? selectedZone?.idZonaSanitaria),
      idAreaProgramatica: current.idAreaProgramatica,
    }));
  }, [
    defaultScope,
    filters.patologia,
    filters.zona,
    options.patologias,
    options.zonas,
  ]);

  useEffect(() => {
    if (!zonaActivaContext || options.zonas.length === 0) return;

    const selectedZone = findZoneByName(options.zonas, zonaActivaContext);
    const activeZoneId = toNumericId(
      selectedZone?.IdZonaSanitaria ?? localStorage.getItem('idZonaActiva')
    );
    if (!activeZoneId) return;

    setFilters((current) => ({
      ...current,
      zona: selectedZone?.Nombre || String(zonaActivaContext),
      area: '',
    }));
    setAppliedScope((current) => ({
      ...current,
      idZonaSanitaria: activeZoneId,
      idAreaProgramatica: null,
    }));
    setDashboardKey((key) => key + 1);
  }, [zonaActivaContext, options.zonas]);

  useEffect(() => {
    const idUsuario = localStorage.getItem('idUsuario');
    if (!idUsuario) return;

    Promise.allSettled([
      api.get(`/patologias-usuario/${idUsuario}`),
      api.get(`/usuarios/${idUsuario}/zonas-asignadas`),
      api.get('/zonas-geograficas/localidades'),
      api.get('/estados'),
      api.get('/areas-programaticas'),
      api.get('/sectores'),
      api.get('/subsectores'),
      api.get('/instituciones-seguimiento-tratamiento'),
    ]).then(([patologias, zonas, localidades, estados, areas, sectores, subsectores, instituciones]) => {
      setOptions({
        patologias: patologias.status === 'fulfilled' ? patologias.value.data : [],
        zonas: zonas.status === 'fulfilled' ? zonas.value.data : [],
        localidades: localidades.status === 'fulfilled' ? localidades.value.data : [],
        estados: estados.status === 'fulfilled' ? estados.value.data : [],
        areas: areas.status === 'fulfilled' ? areas.value.data : [],
        sectores: sectores.status === 'fulfilled' ? sectores.value.data : [],
        subsectores: subsectores.status === 'fulfilled' ? subsectores.value.data : [],
        instituciones: instituciones.status === 'fulfilled' ? instituciones.value.data : [],
      });
    });
  }, []);

  const geographicOptions = useMemo(() => {
    const allLocalidades = Array.isArray(options.localidades) ? options.localidades : [];
    const zonas = Array.isArray(options.zonas) ? options.zonas : [];
    const selectedZone = zonas.find(
      (zone) => String(zone.Nombre).toLocaleLowerCase() ===
        String(filters.zona).toLocaleLowerCase()
    );
    const zoneType = String(selectedZone?.TipoZonaGeografica || '').toLocaleLowerCase();
    const zoneGeographyId = Number(selectedZone?.IdZonaGeografica);
    const localidades = selectedZone && zoneGeographyId
      ? allLocalidades.filter((locality) => {
          if (zoneType === 'localidad') {
            return Number(locality.IdZonaGeografica) === zoneGeographyId;
          }
          if (zoneType === 'partido' || zoneType === 'departamento') {
            return Number(locality.IdPartido) === zoneGeographyId;
          }
          if (zoneType === 'provincia') {
            return Number(locality.IdProvincia) === zoneGeographyId;
          }
          return Number(locality.IdZonaGeografica) === zoneGeographyId;
        })
      : [];
    const unique = (values) => [...new Set(values.filter(Boolean))].sort();
    const provincias = unique([
      ...localidades.map((item) => item.Provincia || item.provincia_nombre),
      ...zonas.map((item) => item.Provincia),
      ...zonas.map(
        (item) => LOCALITY_PROVINCE_FALLBACK[String(item.Localidad || '').toLocaleLowerCase()]
      ),
    ]);
    const localidadesPorProvincia = filters.provincia
      ? localidades.filter(
          (item) => (item.Provincia || item.provincia_nombre) === filters.provincia
        )
      : localidades;
    const selectedDepartments = Array.isArray(filters.departamento)
      ? filters.departamento
      : [filters.departamento].filter(Boolean);
    const localidadesPorDepartamento = selectedDepartments.length
      ? localidadesPorProvincia.filter(
          (item) => selectedDepartments.includes(item.Partido || item.departamento_nombre)
        )
      : localidadesPorProvincia;

    return {
      provincias,
      departamentos: unique(
        localidadesPorProvincia.map((item) => item.Partido || item.departamento_nombre)
      ),
      municipios: unique(
        localidadesPorDepartamento.map((item) => item.Nombre || item.nombre || item.Localidad)
      ),
    };
  }, [options.localidades, options.zonas, filters.zona, filters.provincia, filters.departamento]);

  useEffect(() => {
    if (!filters.zona || options.zonas.length === 0) return;

    const selectedZone = findZoneByName(options.zonas, filters.zona);
    if (!selectedZone) return;

    const zoneLocality = selectedZone.Localidad;
    const catalogLocality = options.localidades.find((locality) => {
      const localityName = locality.Nombre || locality.nombre || locality.Localidad;
      const localityProvince = locality.Provincia || locality.provincia_nombre;
      return (
        zoneLocality &&
        localityProvince &&
        String(localityName).toLocaleLowerCase() === String(zoneLocality).toLocaleLowerCase()
      );
    });

    const zoneType = String(selectedZone.TipoZonaGeografica || '').toLocaleLowerCase();
    const nextProvince =
      (zoneType === 'provincia' ? selectedZone.Localidad : selectedZone.Provincia) ||
      catalogLocality?.Provincia ||
      catalogLocality?.provincia_nombre ||
      LOCALITY_PROVINCE_FALLBACK[String(selectedZone.Localidad || '').toLocaleLowerCase()] ||
      '';
    // La zona ya restringe territorialmente los datos. Departamento y
    // municipio quedan disponibles como filtros dependientes, pero no deben
    // autoseleccionarse: sus nombres normalizados pueden diferir del nombre
    // administrativo de la zona (por ejemplo, Mar del Plata / General
    // Pueyrredón) y la intersección dejaría el dashboard sin resultados.
    const nextDepartment = [];
    const nextMunicipality = [];

    setFilters((current) => {
      if (
        nextProvince === current.provincia &&
        JSON.stringify(nextDepartment) === JSON.stringify(current.departamento) &&
        JSON.stringify(nextMunicipality) === JSON.stringify(current.municipio)
      ) {
        return current;
      }

      return {
        ...current,
        provincia: nextProvince,
        departamento: nextDepartment,
        municipio: nextMunicipality,
      };
    });
    setAppliedFilters((current) => ({
      ...current,
      zona: selectedZone.Nombre,
      provincia: nextProvince,
      departamento: nextDepartment,
      municipio: nextMunicipality,
    }));
  }, [filters.zona, options.zonas, options.localidades]);

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 6 }, (_, index) => String(currentYear - index));
  }, []);

  const areasDisponibles = useMemo(() => {
    return options.areas.filter((area) => Number(area.Estado) === 1);
  }, [options.areas]);

  const subsectoresDisponibles = useMemo(() => {
    const selectedSector = options.sectores.find(
      (sector) => String(sector.Nombre).toLocaleLowerCase() ===
        String(filters.sector).toLocaleLowerCase()
    );
    return options.subsectores.filter(
      (subsector) => Number(subsector.Estado) === 1 &&
        (!selectedSector || Number(subsector.IdSector) === Number(selectedSector.IdSector))
    );
  }, [filters.sector, options.sectores, options.subsectores]);

  const filterDefinitions = [
    { key: 'patologia', label: 'Patología', values: options.patologias.map((item) => item.Nombre) },
    { key: 'anio', label: 'Año epidemiológico', values: yearOptions },
    { key: 'semana', label: 'Semana epidemiológica', values: Array.from({ length: 53 }, (_, index) => String(index + 1)), multiple: true },
    { key: 'provincia', label: 'Provincia', values: geographicOptions.provincias },
    { key: 'departamento', label: 'Departamento', values: geographicOptions.departamentos, multiple: true },
    { key: 'municipio', label: 'Municipio', values: geographicOptions.municipios, multiple: true },
    { key: 'zona', label: 'Zona sanitaria', values: options.zonas.map((item) => item.Nombre) },
    { key: 'area', label: 'Área programática', values: areasDisponibles.map((item) => item.Nombre) },
    { key: 'efector', label: 'Efector', values: options.instituciones.filter((item) => Number(item.Estado) === 1).map((item) => item.Nombre) },
    { key: 'unidad', label: 'Unidad notificadora', values: ['Todas'], fixed: true },
    { key: 'sector', label: 'Sector', values: options.sectores.filter((item) => Number(item.Estado) === 1).map((item) => item.Nombre) },
    { key: 'subsector', label: 'Subsector', values: subsectoresDisponibles.map((item) => item.Nombre) },
    { key: 'estadoCaso', label: 'Estado del caso', values: options.estados.map((item) => item.Nombre) },
    { key: 'estadoSeguimiento', label: 'Estado del seguimiento', values: options.estados.map((item) => item.Nombre) },
  ];

  const updateFilter = (key, value) => {
    setFilters((current) => {
      if (key === 'provincia') {
        return { ...current, provincia: value, departamento: [], municipio: [] };
      }
      if (key === 'departamento') {
        return { ...current, departamento: value, municipio: [] };
      }
      if (key === 'zona') {
        return { ...current, zona: value, area: '' };
      }
      if (key === 'sector') {
        return { ...current, sector: value, subsector: '' };
      }
      return { ...current, [key]: value };
    });
  };

  const clearFilters = () => {
    setFilters((current) => ({
      ...Object.keys(current).reduce((result, key) => ({ ...result, [key]: '' }), {}),
      patologia: localStorage.getItem('patologiaActiva') || '',
      zona: localStorage.getItem('zonaActiva') || '',
      semana: [], departamento: [], municipio: [],
    }));
    setAppliedScope(defaultScope);
    setAppliedFilters((current) => ({
      ...current,
      patologia: localStorage.getItem('patologiaActiva') || '',
      anio: '', semana: [], provincia: '', departamento: [], municipio: [],
      zona: localStorage.getItem('zonaActiva') || '', area: '',
      efector: '', sector: '', subsector: '', estadoCaso: '', estadoSeguimiento: '',
    }));
    setDashboardKey((key) => key + 1);
  };

  const applyFilters = () => {
    const selectedPathology = options.patologias.find(
      (item) => String(item.Nombre).toLocaleLowerCase() === filters.patologia.toLocaleLowerCase()
    );
    const selectedZone = findZoneByName(options.zonas, filters.zona);
    const selectedArea = areasDisponibles.find(
      (item) => String(item.Nombre).toLocaleLowerCase() === filters.area.toLocaleLowerCase()
    );

    setAppliedScope({
      idUsuario: defaultScope.idUsuario,
      idPatologia: toNumericId(
        selectedPathology?.idPatologia ??
          selectedPathology?.IdPatologia ??
          defaultScope.idPatologia
      ),
      idZonaSanitaria: toNumericId(
        selectedZone?.IdZonaSanitaria ??
          selectedZone?.idZonaSanitaria
      ),
      idAreaProgramatica: toNumericId(selectedArea?.IdAreaProgramatica),
    });
    setAppliedFilters({ ...filters });
    setDashboardKey((key) => key + 1);
    if (isMobile) setFiltersVisible(false);
  };

  const handleShare = async () => {
    const shareData = {
      title: 'VEC - Panel de Estadísticas Epidemiológicas',
      url: window.location.href,
    };

    if (navigator.share) {
      await navigator.share(shareData);
      return;
    }

    await navigator.clipboard.writeText(shareData.url);
  };

  return (
    <main className="superset-shell">
      <header className="superset-shell__header">
        <div className="superset-shell__identity">
          <button className="superset-shell__icon-button" type="button" aria-label="Abrir menú" aria-expanded={menuOpen} onClick={() => setMenuOpen((open) => !open)}>
            <FontAwesomeIcon icon={faBars} />
          </button>
          {menuOpen && (
            <div className="superset-shell__menu">
              <strong>Tableros epidemiológicos</strong>
              <button className={activeView === 'dashboard' ? 'is-active' : ''} type="button" onClick={() => { setActiveView('dashboard'); setMenuOpen(false); }}><span>Resumen ejecutivo</span><small>Dashboard actual de Superset</small></button>
              <button className={activeView === 'situation' ? 'is-active' : ''} type="button" onClick={() => { setActiveView('situation'); setMenuOpen(false); }}><span>Situación epidemiológica</span><small>Indicadores, tendencias y resultados</small></button>
              <button className={activeView === 'contacts' ? 'is-active' : ''} type="button" onClick={() => { setActiveView('contacts'); setMenuOpen(false); }}><span>Investigación y seguimiento de contactos</span><small>Contactos, alertas y estado operativo</small></button>
              <button className={activeView === 'territorial' ? 'is-active' : ''} type="button" onClick={() => { setActiveView('territorial'); setMenuOpen(false); }}><span>Análisis territorial</span><small>Distribución, comparación y tendencia</small></button>
              <button className={activeView === 'sector' ? 'is-active' : ''} type="button" onClick={() => { setActiveView('sector'); setMenuOpen(false); }}><span>Sector, subsector y efectores</span><small>Distribución y carga institucional</small></button>
              <button className={activeView === 'quality' ? 'is-active' : ''} type="button" onClick={() => { setActiveView('quality'); setMenuOpen(false); }}><span>Calidad de datos</span><small>Completitud, alertas y validaciones</small></button>
              <button className={activeView === 'pathology' ? 'is-active' : ''} type="button" onClick={() => { setActiveView('pathology'); setMenuOpen(false); }}><span>Indicadores específicos por patología</span><small>Variables e indicadores programáticos</small></button>
              <button className={activeView === 'download' ? 'is-active' : ''} type="button" onClick={() => { setActiveView('download'); setMenuOpen(false); }}><span>Descarga del resumen completo</span><small>PDF, Excel/CSV y programación</small></button>
            </div>
          )}
          <img className="superset-shell__logo" src={logo} alt="Ministerio de Salud" />
          <span className="superset-shell__divider" aria-hidden="true" />
          <div>
            <h1>VEC - Panel de Estadísticas Epidemiológicas</h1>
            <p>
              {activePathology ? `${activePathology} · ` : ''}
              Diseñado para Apache Superset
            </p>
          </div>
        </div>

        <div className="superset-shell__actions">
          <button
            className="superset-shell__action superset-shell__back"
            type="button"
            onClick={() => navigate('/PrivateMainPage')}
            title="Volver al sistema VEC"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>Volver a VEC</span>
          </button>
          <a
            className="superset-shell__action"
            href="https://superset.apache.org/docs/using-superset/exploring-data/"
            target="_blank"
            rel="noreferrer"
          >
            <FontAwesomeIcon icon={faCircleQuestion} />
            <span>Ayuda</span>
          </a>
          <a className="superset-shell__action" href={dashboardUrl} target="_blank" rel="noreferrer">
            <FontAwesomeIcon icon={faDownload} />
            <span>Abrir</span>
          </a>
          <button className="superset-shell__action" type="button" onClick={handleShare}>
            <FontAwesomeIcon icon={faShareNodes} />
            <span>Compartir</span>
          </button>
          <button
            className="superset-shell__user"
            type="button"
            title={userName}
            aria-label={`Usuario: ${userName}`}
          >
            {initials || <FontAwesomeIcon icon={faUser} />}
          </button>
        </div>
      </header>

      <section className="superset-shell__filters">
        <button
          className="superset-shell__filter-toggle"
          type="button"
          onClick={() => setFiltersVisible((visible) => !visible)}
        >
          <FontAwesomeIcon icon={faSliders} />
          Filtros aplicados
        </button>
        <div className="superset-shell__filter-chips">
          {filterDefinitions
            .filter(({ key, fixed }) =>
              !fixed && (Array.isArray(filters[key]) ? filters[key].length > 0 : filters[key])
            )
            .map(({ key, label }) => (
              <button key={key} type="button" onClick={() => updateFilter(key, Array.isArray(filters[key]) ? [] : '')}>
                <span>{label}:</span> {Array.isArray(filters[key]) ? filters[key].join(', ') : filters[key]} ×
              </button>
            ))}
        </div>
        <button type="button" onClick={clearFilters}>
          Limpiar filtros
        </button>
      </section>

      <div className={`superset-shell__content ${filtersVisible ? '' : 'superset-shell__content--collapsed'}`}>
        {isMobile && filtersVisible && (
          <button
            className="superset-shell__sidebar-backdrop"
            type="button"
            aria-label="Cerrar filtros"
            onClick={() => setFiltersVisible(false)}
          />
        )}
        {filtersVisible && (
          <aside className="superset-shell__sidebar">
            <div className="superset-shell__sidebar-title">
              <strong>Filtros</strong>
              <button type="button" onClick={() => setFiltersVisible(false)}>Ocultar ‹</button>
            </div>
            <div className="superset-shell__filter-list">
              {filterDefinitions.map(({ key, label, values, multiple }) => (
                <div className="superset-filter-row" key={key}>
                  <span>{label}</span>
                  {multiple ? (
                    <MultiFilterSelect
                      label={label}
                      values={values}
                      selected={filters[key]}
                      onChange={(value) => updateFilter(key, value)}
                    />
                  ) : (
                    <select
                      value={filters[key]}
                      onChange={(event) => updateFilter(key, event.target.value)}
                    >
                      <option value="">Todos</option>
                      {values.map((value) => <option key={value} value={value}>{value}</option>)}
                    </select>
                  )}
                </div>
              ))}
            </div>
            <button className="superset-shell__apply" type="button" onClick={applyFilters}>
              <FontAwesomeIcon icon={faSliders} />
              Aplicar filtros
            </button>
          </aside>
        )}
        <div className={`superset-shell__dashboard-frame ${activeView !== 'dashboard' || isMobile ? 'superset-shell__dashboard-frame--situation' : 'superset-shell__dashboard-frame--dashboard'}`}>
          {activeView === 'dashboard' && isMobile ? <SupersetSituationView summaryMode onNavigate={setActiveView} pathology={appliedFilters.patologia} scope={appliedScope} appliedFilters={appliedFilters} catalogOptions={options} /> : activeView === 'dashboard' ? <>
          <h1 className="superset-shell__dashboard-title">Resumen ejecutivo</h1>
          <div className="superset-shell__dashboard-viewport">
          <div className="superset-shell__zoom" aria-label="Zoom del panel">
            <button
              type="button"
              onClick={() => setDashboardZoom((zoom) => Math.max(1, +(zoom - 0.15).toFixed(2)))}
              disabled={dashboardZoom <= 1}
              title="Alejar panel"
              aria-label="Alejar panel"
            >
              −
            </button>
            <button
              className="superset-shell__zoom-value"
              type="button"
              onClick={() => setDashboardZoom(1)}
              title="Restablecer zoom"
              aria-label="Restablecer zoom al 100%"
            >
              {Math.round(dashboardZoom * 100)}%
            </button>
            <button
              type="button"
              onClick={() => setDashboardZoom((zoom) => Math.min(1.6, +(zoom + 0.15).toFixed(2)))}
              disabled={dashboardZoom >= 1.6}
              title="Ampliar panel"
              aria-label="Ampliar panel"
            >
              +
            </button>
          </div>
          <div
          key={dashboardKey}
          ref={dashboardMountRef}
          className="superset-shell__dashboard"
          style={{
            width: `${100 / dashboardZoom}%`,
            height: `${100 / dashboardZoom}%`,
            transform: `scale(${dashboardZoom})`,
          }}
          />
          </div>
          </> : activeView === 'situation' ? <SupersetSituationView onNavigate={setActiveView} pathology={appliedFilters.patologia} scope={appliedScope} appliedFilters={appliedFilters} catalogOptions={options} /> : activeView === 'contacts' ? <SupersetContactsView scope={appliedScope} appliedFilters={appliedFilters} catalogOptions={options} /> : activeView === 'territorial' ? <SupersetTerritorialView scope={appliedScope} appliedFilters={appliedFilters} catalogOptions={options} /> : activeView === 'sector' ? <SupersetSectorView scope={appliedScope} appliedFilters={appliedFilters} catalogOptions={options} /> : activeView === 'quality' ? <SupersetDataQualityView scope={appliedScope} appliedFilters={appliedFilters} catalogOptions={options} /> : activeView === 'pathology' ? <SupersetPathologyView pathology={appliedFilters.patologia} scope={appliedScope} appliedFilters={appliedFilters} catalogOptions={options} /> : <SupersetDownloadView scope={appliedScope} appliedFilters={appliedFilters} catalogOptions={options} />}
        </div>
      </div>
    </main>
  );
};

export default ApacheSuperset;
